/**
 * KAN-776: ход партии архивов: GET import-batch/{id}/ раз в 3 с, пока
 * status queued или running; строки со статусами и ошибками по-русски,
 * причина остановки партии (stopped_reason). Когда партия завершена, список
 * аккаунтов обновляется (тот же ключ, что после одиночного импорта) и кнопка
 * "Готово" закрывает диалог. Ошибка запроса (404 batch_unknown, сеть):
 * опрос останавливается, текст ошибки и кнопка "Закрыть".
 * KAN-777: у пропущенного дубля (duplicate_in_batch) причина с именем первого
 * файла: текст сервера, иначе describeBatchItemError дописывает duplicate_of.
 */
import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  BATCH_ITEM_STATUS_LABELS,
  BATCH_POLL_MS,
  batchProgress,
  describeBatchItemError,
  describeBatchStatusError,
  describeStoppedReason,
  isBatchActive,
} from '@/lib/telegramImportBatch';
import { telegramAccountsApi } from '@/services/api/telegramAccounts';
import type { BatchItemStatus } from '@/types/telegramAccount';

const STATUS_CLASS: Record<BatchItemStatus, string | undefined> = {
  queued: 'text-gray-500',
  running: 'text-blue-700',
  imported: 'text-green-700',
  skipped: 'text-amber-700',
  failed: 'text-red-600',
  not_started: 'text-gray-400',
};

interface Props {
  batchId: string;
  onDone: () => void;
}

export function BatchProgress({ batchId, onDone }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const { data, error, isError } = useQuery({
    queryKey: ['telegram-import-batch', batchId],
    queryFn: () => telegramAccountsApi.getBatchImport(batchId),
    retry: false,
    // до первого ответа статус неизвестен: опрашиваем; после ошибки запроса не опрашиваем
    refetchInterval: (query) => {
      if (query.state.status === 'error') return false;
      return !query.state.data || isBatchActive(query.state.data.status) ? BATCH_POLL_MS : false;
    },
  });

  const finished = data?.status === 'done';
  const stopped = describeStoppedReason(data?.stopped_reason);
  useEffect(() => {
    if (finished) {
      void queryClient.invalidateQueries({ queryKey: ['telegram-accounts'] });
    }
  }, [finished, queryClient]);

  const items = data?.items ?? [];
  const progress = batchProgress(items);

  return (
    <div className="space-y-3" data-testid="batch-progress">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium" data-testid="batch-progress-total">
          {finished ? 'Партия завершена' : 'Подключаем аккаунты'}: подключено {progress.imported}{' '}
          из {progress.total}
          {progress.failed > 0 && `, ошибок: ${progress.failed}`}
        </span>
        {!finished && !isError && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
      </div>
      <Progress value={progress.percent} aria-label="Ход партии" />
      {stopped && (
        <p className="text-sm text-red-600" data-testid="batch-progress-stopped">
          {stopped}
        </p>
      )}
      {error && (
        <p className="text-sm text-red-600" data-testid="batch-progress-error">
          {describeBatchStatusError(error)}
        </p>
      )}
      <Table className="text-xs">
        <TableHeader>
          <TableRow>
            <TableHead>Файл</TableHead>
            <TableHead>Номер</TableHead>
            <TableHead>Метка</TableHead>
            <TableHead>Прокси</TableHead>
            <TableHead>Статус</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => {
            const itemError = describeBatchItemError(item);
            return (
              <TableRow key={item.index} data-testid={`batch-progress-row-${item.index}`}>
                <TableCell className="max-w-[10rem] truncate" title={item.file_name}>
                  {item.file_name}
                </TableCell>
                <TableCell>{item.phone || '-'}</TableCell>
                <TableCell>{item.label || '-'}</TableCell>
                <TableCell>{item.proxy_label || '-'}</TableCell>
                <TableCell className={STATUS_CLASS[item.status]}>
                  {BATCH_ITEM_STATUS_LABELS[item.status] ?? item.status}
                  {itemError && <span className="block text-red-600">{itemError}</span>}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <div className="flex justify-end">
        <Button data-testid="batch-done-button" disabled={!finished && !isError} onClick={onDone}>
          {isError && !finished ? 'Закрыть' : 'Готово'}
        </Button>
      </div>
    </div>
  );
}
